import { getParentPathWithoutFilters } from 'src/services/contentstack/taxonomyNeighborhood/getParentPathWithoutFilters';
import { extractBasePath, FILTER_PATH_SEPARATOR } from 'src/utils/extractBasePath';

/** Minimum shape for taxonomy URL matching (neighborhood stubs or full `taxonomy_plp` entries). */
export interface SelectableTaxonomyEntry {
  url: string;
}

/**
 * Strips a URL down to its path shape: drops query/hash and a trailing empty
 * `/-/`, and normalises the trailing slash. Case and percent-encoding are
 * preserved, so this is safe for emitting a canonical href as well as for
 * feeding {@link normaliseForMatch}. The single source of truth for the
 * path-shape rules shared by URL matching and canonical construction.
 */
export const stripToPath = (url: string): string => {
  const pathOnly = url.split('?')[0].split('#')[0];
  const withoutEmptyFilters = pathOnly.endsWith(FILTER_PATH_SEPARATOR)
    ? pathOnly.slice(0, -FILTER_PATH_SEPARATOR.length)
    : pathOnly;

  return withoutEmptyFilters.length <= 1
    ? withoutEmptyFilters
    : withoutEmptyFilters.replace(/\/+$/, '');
};

/** Normalises a taxonomy URL for equality checks (current path and map entries). */
export const normaliseForMatch = (url: string): string => {
  const structural = stripToPath(url);

  try {
    return decodeURIComponent(structural.replace(/\+/g, '%20')).toLowerCase();
  } catch {
    return structural.replace(/\+/g, ' ').toLowerCase();
  }
};

export const findEntryByUrl = <T extends SelectableTaxonomyEntry>(
  entries: T[],
  targetUrl: string,
): T | null => {
  const target = normaliseForMatch(targetUrl);

  return entries.find((entry) => normaliseForMatch(entry.url) === target) ?? null;
};

/**
 * Picks the best neighborhood entry for a page URL (first match wins).
 * The map spans the current page's ancestors, its siblings, and its children.
 * 1. `entry.url` equals the normalised current path (full URL, including filters)
 * 2. `entry.url` equals the current path taxonomy base (`extractBasePath` on current path only)
 * 3. `entry.url` equals the parent taxonomy base of the current base path
 */
export const selectTaxonomyContentEntry = <T extends SelectableTaxonomyEntry>(
  currentPath: string,
  entries: T[],
): T | null => {
  if (entries.length === 0) {
    return null;
  }

  const currentBasePath = extractBasePath(currentPath);
  const exactMatch = findEntryByUrl(entries, currentPath);
  if (exactMatch) {
    return exactMatch;
  }

  const currentBaseMatch = findEntryByUrl(entries, currentBasePath);
  if (currentBaseMatch) {
    return currentBaseMatch;
  }

  const parentBasePath = getParentPathWithoutFilters(currentBasePath);
  if (!parentBasePath || parentBasePath === currentBasePath) {
    return null;
  }

  return findEntryByUrl(entries, parentBasePath);
};
